import {useMemo, useState} from 'react'
import {Pressable, useWindowDimensions, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {Trans} from '@lingui/react/macro'
import {useLingui} from '@lingui/react'

import {atoms as a, useTheme} from '#/alf'
import * as Dialog from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {ChevronBottom_Stroke2_Corner0_Rounded as ChevronDown} from '#/components/icons/Chevron'
import {MagnifyingGlass_Stroke2_Corner0_Rounded as MagnifyingGlassIcon} from '#/components/icons/MagnifyingGlass'
import {PinLocation_Stroke2_Corner0_Rounded as PinLocation} from '#/components/icons/PinLocation'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {getRegionByCode, type Region, TREND_REGIONS} from '../data/regions'

/**
 * Region selector for the "Regional" trends scope. Renders a pill showing the
 * active country (📍 <country> ⌄, Figma node `384:53814`) that opens a
 * searchable dialog listing all selectable regions (European countries, a
 * whole-continent "Europe" option, and the United States).
 *
 * The selected `code` is an ISO 3166-1 alpha-2 country code; the parent feeds it
 * to `GET /trends?scope=region&region=<code>` (see
 * `.agents/repos/root/trends/trends-how-it-works.md`).
 */
export function RegionPicker({
  selectedCode,
  onSelect,
}: {
  selectedCode: string
  onSelect: (code: string) => void
}) {
  const t = useTheme()
  const ss = useSemanticTokens()
  const {_} = useLingui()
  const control = Dialog.useDialogControl()

  const selected = getRegionByCode(selectedCode)

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={_(msg`Select region`)}
        accessibilityHint={_(msg`Opens a list of countries to choose from`)}
        onPress={control.open}
        style={[
          a.flex_row,
          a.align_center,
          a.gap_xs,
          a.rounded_full,
          a.border,
          {
            height: 34,
            paddingHorizontal: 12,
            borderColor: ss.border.contrast_low,
            backgroundColor: ss.bg.default,
          },
        ]}>
        <PinLocation size="xs" style={t.atoms.text_contrast_medium} />
        <Text style={[a.text_xs, a.font_bold, t.atoms.text]}>
          {selected?.name ?? _(msg`Select region`)}
        </Text>
        <ChevronDown size="xs" style={t.atoms.text_contrast_medium} />
      </Pressable>

      <Dialog.Outer control={control}>
        <Dialog.Handle />
        <RegionPickerDialog
          selectedCode={selectedCode}
          onSelect={code => {
            onSelect(code)
            control.close()
          }}
        />
      </Dialog.Outer>
    </>
  )
}

function RegionPickerDialog({
  selectedCode,
  onSelect,
}: {
  selectedCode: string
  onSelect: (code: string) => void
}) {
  const {_} = useLingui()
  const {height: windowHeight} = useWindowDimensions()
  const [query, setQuery] = useState('')

  // Reserve a stable height for the list so filtering down to a few results
  // doesn't visibly collapse the sheet as the user types. The full list is
  // taller than this and scrolls; short result sets keep the reserved space.
  const listMinHeight = Math.round(windowHeight * 0.6)

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return TREND_REGIONS
    return TREND_REGIONS.filter(
      c => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q,
    )
  }, [query])

  return (
    <Dialog.ScrollableInner label={_(msg`Select a region`)}>
      <View style={[a.gap_md]}>
        <Text style={[a.text_lg, a.font_bold]}>
          <Trans>Select a region</Trans>
        </Text>

        <TextField.Root>
          <TextField.Icon icon={MagnifyingGlassIcon} />
          <TextField.Input
            label={_(msg`Search countries`)}
            placeholder={_(msg`Search countries`)}
            value={query}
            onChangeText={setQuery}
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="search"
          />
        </TextField.Root>

        <View style={{minHeight: listMinHeight}}>
          {results.map(region => (
            <RegionRow
              key={region.code}
              region={region}
              isSelected={region.code === selectedCode}
              onPress={() => onSelect(region.code)}
            />
          ))}
          {results.length === 0 && (
            <Text
              style={[a.text_sm, a.pt_md, a.text_center]}
              accessibilityRole="text">
              <Trans>No countries found</Trans>
            </Text>
          )}
        </View>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}

function RegionRow({
  region,
  isSelected,
  onPress,
}: {
  region: Region
  isSelected: boolean
  onPress: () => void
}) {
  const t = useTheme()
  const ss = useSemanticTokens()
  const {_} = useLingui()

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{selected: isSelected}}
      accessibilityLabel={region.name}
      accessibilityHint={_(msg`Filters trends to this region`)}
      onPress={onPress}
      style={[
        a.flex_row,
        a.align_center,
        a.justify_between,
        a.px_sm,
        a.rounded_sm,
        {
          height: 44,
          backgroundColor: isSelected ? ss.bg.contrast_25 : 'transparent',
        },
      ]}>
      <Text
        style={[
          a.text_md,
          isSelected ? a.font_bold : a.font_normal,
          t.atoms.text,
        ]}>
        {region.name}
      </Text>
      <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
        {region.code}
      </Text>
    </Pressable>
  )
}
